import { type CSSProperties, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { Label } from '@/entities/board';
import { addDays, weekOf } from '@/shared/lib/calendar';
import { formatCalendarDate } from '@/shared/lib/datetime';

import styles from './DaySchedule.module.css';
import { barStyle, itemFill, labelColorsOf } from './labels';
import { type CalendarEvent, type DayLoad, floatingNow, splitLocal } from './model';
import { StateTag } from './StateTag';
import { Workload } from './Workload';
import {
  allDayOf,
  firstVisibleHour,
  hasItems,
  MINUTES_PER_DAY,
  timedOf,
  type TimedItem,
} from './schedule';

/** Height of one hour on the axis, in rem (the CSS uses the same custom property). */
const HOUR_REM = 3.5;
/** Horizontal distance of a swipe that changes the day. */
const SWIPE_PX = 60;

interface Props {
  /** The selected day, `YYYY-MM-DD`. */
  date: string;
  events: readonly CalendarEvent[];
  today: string;
  /** 0 = Monday ... 6 = Sunday. */
  firstDay: number;
  locale: string;
  timeZone: string | null;
  colorOf: (boardId: string) => string;
  /** "Board · Workspace" of an item's source, or '' when every item comes from one board. */
  sourceOf: (boardId: string) => string;
  labels: readonly Label[];
  /** Daily workload under each day of the strip (null = off). */
  workload?: ReadonlyMap<string, DayLoad> | null;
  onSelectDate: (date: string) => void;
  onOpenCard: (cardId: string) => void;
}

function format(minutes: number): string {
  const hours = Math.floor(minutes / 60) % 24;
  return `${String(hours).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/**
 * The phone's day: a date strip, the date-only items ("All day") and an hourly timeline holding
 * only items with a real time. A task due on a date without a time is never drawn at an hour:
 * it has no time to be at.
 */
export function DaySchedule({
  date,
  events,
  today,
  firstDay,
  locale,
  timeZone,
  colorOf,
  sourceOf,
  labels,
  workload = null,
  onSelectDate,
  onOpenCard,
}: Props) {
  const { t } = useTranslation();
  const week = weekOf(date, firstDay);
  const strip = Array.from({ length: 7 }, (_, index) => {
    const day = new Date(`${week.first}T00:00:00Z`);
    day.setUTCDate(day.getUTCDate() + index);
    return day.toISOString().slice(0, 10);
  });
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  const weekdayLong = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' });
  const allDay = allDayOf(events, date);
  const timed = timedOf(events, date);
  const scroller = useRef<HTMLDivElement>(null);
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const firstHour = firstVisibleHour(timed);

  // Open on the first item (or the working day); a new day starts from there again.
  useEffect(() => {
    const element = scroller.current;
    if (element) {
      const rem = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      element.scrollTop = firstHour * HOUR_REM * rem;
    }
  }, [date, firstHour]);

  const now = date === today ? floatingNow(timeZone) : null;
  const nowMinutes = now ? now.getUTCHours() * 60 + now.getUTCMinutes() : null;

  const renderBar = (event: CalendarEvent) => (
    <span
      className={styles.bar}
      style={barStyle(itemFill(labelColorsOf(event.card, labels)), colorOf(event.boardId))}
      aria-hidden
    />
  );

  return (
    <div
      className={styles.schedule}
      data-testid="day-schedule"
      data-date={date}
      // A horizontal swipe moves to the next / previous day; vertical scrolling is untouched.
      onTouchStart={(event) => {
        const touch = event.touches[0];
        swipeStart.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
      }}
      onTouchEnd={(event) => {
        const start = swipeStart.current;
        const touch = event.changedTouches[0];
        swipeStart.current = null;
        if (!start || !touch) return;
        const dx = touch.clientX - start.x;
        const dy = touch.clientY - start.y;
        if (Math.abs(dx) >= SWIPE_PX && Math.abs(dx) > Math.abs(dy) * 1.5) {
          onSelectDate(addDays(date, dx < 0 ? 1 : -1));
        }
      }}
    >
      <div className={styles.strip} role="group" aria-label={t('calendar.dayStrip')}>
        {strip.map((day) => (
          <button
            key={day}
            type="button"
            className={`${styles.stripDay} ${day === date ? styles.selected : ''}`}
            aria-pressed={day === date}
            aria-current={day === today ? 'date' : undefined}
            aria-label={`${weekdayLong.format(new Date(`${day}T00:00:00Z`))}, ${formatCalendarDate(day, locale)}`}
            data-day={day}
            onClick={() => {
              onSelectDate(day);
            }}
          >
            <span className={styles.stripWeekday}>
              {weekday.format(new Date(`${day}T00:00:00Z`))}
            </span>
            <span className={styles.stripNumber}>{Number(day.slice(8, 10))}</span>
            {workload ? (
              <span className={styles.stripLoad}>
                <Workload load={workload.get(day)} compact />
              </span>
            ) : (
              <span
                className={`${styles.stripDot} ${hasItems(events, day) ? styles.stripDotOn : ''}`}
                aria-hidden
              />
            )}
          </button>
        ))}
      </div>

      <section className={styles.allDay} aria-label={t('calendar.allDaySection')}>
        <h3 className={styles.heading}>{t('calendar.allDaySection')}</h3>
        {allDay.length === 0 ? (
          <p className={styles.empty}>{t('calendar.noAllDay')}</p>
        ) : (
          <ul className={styles.allDayList} data-testid="all-day-list">
            {allDay.map((event) => {
              const done = event.card.completed_at !== null;
              const source = sourceOf(event.boardId);
              return (
                <li key={event.id}>
                  <button
                    type="button"
                    className={`${styles.entry} ${done ? styles.done : ''} ${event.phase === 'past' && !done ? styles.past : ''}`}
                    data-testid="schedule-all-day-item"
                    onClick={() => {
                      onOpenCard(event.id);
                    }}
                  >
                    {renderBar(event)}
                    <span className={styles.text}>
                      <span className={styles.title}>{event.card.title}</span>
                      <span className={styles.meta}>
                        {[source, event.kind === 'deadline' ? t('calendar.deadline') : '']
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                      <StateTag event={event} />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className={styles.timeline} aria-label={t('calendar.timeline')}>
        <h3 className={styles.heading}>{t('calendar.timeline')}</h3>
        <div ref={scroller} className={styles.scroller} data-testid="schedule-scroller">
          <div className={styles.axis} style={{ '--hour-rem': `${HOUR_REM}rem` } as CSSProperties}>
            {Array.from({ length: 24 }, (_, hour) => (
              <div key={hour} className={styles.hour} style={{ top: `${hour * HOUR_REM}rem` }}>
                <span className={styles.hourLabel}>{format(hour * 60)}</span>
              </div>
            ))}
            {nowMinutes !== null && (
              <div
                className={styles.now}
                style={{ top: `${(nowMinutes / 60) * HOUR_REM}rem` }}
                aria-hidden
              />
            )}
            {timed.map((item) => (
              <TimedEntry
                key={item.event.id}
                item={item}
                done={item.event.card.completed_at !== null}
                source={sourceOf(item.event.boardId)}
                bar={renderBar(item.event)}
                dueLabel={t('calendar.dueAt', { time: splitLocal(item.event.start).time })}
                onOpen={() => {
                  onOpenCard(item.event.id);
                }}
              />
            ))}
          </div>
        </div>
        {timed.length === 0 && <p className={styles.empty}>{t('calendar.noTimed')}</p>}
      </section>
    </div>
  );
}

function TimedEntry({
  item,
  done,
  source,
  bar,
  dueLabel,
  onOpen,
}: {
  item: TimedItem;
  done: boolean;
  source: string;
  bar: React.ReactNode;
  dueLabel: string;
  onOpen: () => void;
}) {
  const { event } = item;
  const style: CSSProperties = {
    top: `${(item.start / 60) * HOUR_REM}rem`,
    height: `${((item.end - item.start) / 60) * HOUR_REM}rem`,
    left: `calc(var(--gutter-width) + (100% - var(--gutter-width)) * ${String(item.lane / item.lanes)})`,
    width: `calc((100% - var(--gutter-width)) / ${String(item.lanes)} - 2px)`,
  };
  const range = item.point
    ? dueLabel
    : `${format(item.start)} – ${format(item.end >= MINUTES_PER_DAY ? 0 : item.end)}`;
  return (
    <button
      type="button"
      className={`${styles.block} ${item.point ? styles.point : ''} ${done ? styles.done : ''} ${
        event.phase === 'past' && !done ? styles.past : ''
      }`}
      style={style}
      data-testid="schedule-timed-item"
      data-start={format(item.start)}
      onClick={onOpen}
    >
      {bar}
      <span className={styles.text}>
        <span className={styles.title}>{event.card.title}</span>
        <span className={styles.meta}>{[range, source].filter(Boolean).join(' · ')}</span>
        <StateTag event={event} />
      </span>
    </button>
  );
}
